import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Check, MessageCircle, Download } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/**
 * Optimistic success screen.
 *
 * Stripe only redirects to this page when the customer has reached the success URL
 * of the checkout — which Stripe issues after the payment intent is confirmed.
 * So we treat the arrival on this page as success and load the booking details
 * silently in the background. If the booking is not yet marked paid in our DB,
 * the background reconciler (every 20s) + auto-recovery on GET /bookings/{id}
 * will fix it within seconds.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { t } = useLang();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const [booking, setBooking] = useState(null);
  const [hasError, setHasError] = useState(false);
  const attemptsRef = useRef(0);

  const load = useCallback(async () => {
    if (!bookingId && !sessionId) { setHasError(true); return; }
    // Try the booking directly first (cheap, instantly available, and our auto-recovery
    // inside GET /bookings/{id} will reconcile against Stripe if still pending).
    if (bookingId) {
      try {
        const r = await api.get(`/bookings/${bookingId}`);
        if (r.data) setBooking(r.data);
        if (r.data?.payment_status === "paid") return; // we're done
      } catch (e) { /* ignore */ }
    }
    // Poll silently up to 90 attempts (3 minutes total) to mark paid. The UI is already in
    // the "success" state so the customer is happy — we just refresh the ticket details
    // once the backend confirms.
    if (attemptsRef.current >= 90) return;
    attemptsRef.current += 1;
    setTimeout(load, 2000);
  }, [sessionId, bookingId]);

  useEffect(() => { load(); }, [load]);

  const shareWhatsApp = () => {
    if (!booking) return;
    const text = `🎟️ *Ticket Benova Travel*\n\n` +
      `*${booking.experience_name}*\n` +
      `Booking: ${booking.id.slice(0, 8).toUpperCase()}\n` +
      `Nombre: ${booking.customer_name}\n` +
      `Fecha: ${booking.booking_date}\n` +
      `Personas: ${booking.num_people}\n` +
      `Total: $${booking.total_amount} ${booking.currency}\n\n` +
      `Verifica: ${window.location.origin}/ticket/${booking.id}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  // Hard error only when there are no identifiers at all
  if (hasError) {
    return (
      <div data-testid="payment-success-page">
        <Navbar />
        <section className="pt-32 pb-20 bn-section">
          <div className="bn-container px-6 md:px-8 max-w-xl mx-auto text-center">
            <h1 className="bn-heading text-3xl">No encontramos tu reserva</h1>
            <p className="mt-4" style={{ color: "var(--bn-text-muted)" }}>
              Por favor contáctanos para ayudarte:
            </p>
            <p className="mt-4">
              <b>WhatsApp:</b>{" "}
              <a href="https://wa.me/5219844083794" target="_blank" rel="noreferrer" style={{ color: "var(--bn-accent)" }}>+52 1 984 408 3794</a>
            </p>
            <Link to="/experiencias" className="bn-btn-primary inline-block mt-6">{t("pay.back_catalog")}</Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="payment-success-page">
      <Navbar />
      <section className="pt-32 pb-20 bn-section">
        <div className="bn-container px-6 md:px-8 max-w-3xl mx-auto">
          <SuccessHero booking={booking} t={t} />
          {booking ? (
            <TicketCard booking={booking} t={t} />
          ) : (
            <SkeletonTicket />
          )}
          <ActionButtons booking={booking} shareWhatsApp={shareWhatsApp} t={t} />
        </div>
      </section>
      <Footer />
    </div>
  );
}

function SuccessHero({ booking, t }) {
  const reconciling = booking && booking.payment_status !== "paid";
  return (
    <div className="text-center mb-10" data-testid="success-hero">
      <div
        className="inline-flex w-16 h-16 rounded-full items-center justify-center"
        style={{
          background: "var(--bn-accent)",
          color: "var(--bn-accent-fg)",
          animation: "bnPop 360ms cubic-bezier(.2,.9,.3,1.2)",
        }}
      >
        <Check size={28} strokeWidth={3} />
      </div>
      <h1 className="bn-heading text-4xl md:text-5xl mt-6">{t("pay.confirmed")}</h1>
      <p className="mt-3" style={{ color: "var(--bn-text-muted)" }}>
        {t("pay.email_sent")}
      </p>
      {reconciling && (
        <p className="mt-2 text-xs" style={{ color: "var(--bn-text-muted)", opacity: 0.7 }}>
          Tu ticket llegará por correo en los próximos segundos…
        </p>
      )}
    </div>
  );
}

function SkeletonTicket() {
  return (
    <div className="bn-card overflow-hidden animate-pulse" data-testid="ticket-skeleton">
      <div className="p-8 md:p-10" style={{ background: "var(--bn-primary)" }}>
        <div className="h-3 w-32 rounded" style={{ background: "rgba(255,255,255,0.15)" }} />
        <div className="h-8 w-3/4 rounded mt-4" style={{ background: "rgba(255,255,255,0.18)" }} />
      </div>
      <div className="p-8 md:p-10 space-y-3">
        <div className="h-3 w-1/2 rounded" style={{ background: "var(--bn-muted)" }} />
        <div className="h-3 w-2/3 rounded" style={{ background: "var(--bn-muted)" }} />
        <div className="h-3 w-1/3 rounded" style={{ background: "var(--bn-muted)" }} />
      </div>
    </div>
  );
}

function TicketCard({ booking, t }) {
  const code = booking.id.slice(0, 8).toUpperCase();
  const currency = booking.currency || "USD";
  const subtotal = Number(booking.total_amount || 0);
  const finalAmount = booking.final_amount !== undefined && booking.final_amount !== null
    ? Number(booking.final_amount)
    : subtotal;
  const hasDiscount = booking.coupon_code && finalAmount < subtotal;
  const discount = subtotal - finalAmount;
  return (
    <div className="bn-card overflow-hidden" data-testid="ticket-card">
      <div className="p-8 md:p-10" style={{ background: "var(--bn-primary)", color: "var(--bn-primary-fg)" }}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="bn-eyebrow opacity-60">Benova Travel Group</div>
            <h2 className="mt-3 text-3xl md:text-4xl" style={{ fontFamily: "var(--bn-font-heading)" }}>{booking.experience_name}</h2>
          </div>
          <div className="text-right">
            <div className="bn-eyebrow opacity-60">{t("pay.code")}</div>
            <div className="mt-2 text-2xl tracking-widest" style={{ fontFamily: "var(--bn-font-heading)" }}>{code}</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-5 gap-0">
        <div className="md:col-span-3 p-8 md:p-10 space-y-5">
          <Row label={t("pay.holder")} value={booking.customer_name} />
          <Row label={t("book.email")} value={booking.customer_email} />
          <Row label={t("pay.phone")} value={booking.customer_phone} />
          <Row label={t("detail.date")} value={booking.booking_date} />
          <Row label={t("detail.people")} value={String(booking.num_people)} />
          {hasDiscount && (
            <>
              <div style={{ borderTop: "1px dashed var(--bn-border)" }} />
              <Row label="Subtotal" value={`$${subtotal.toFixed(2)} ${currency}`} muted />
              <Row label="Cupón aplicado" value={booking.coupon_code} accent />
              <Row label="Descuento" value={`- $${discount.toFixed(2)} ${currency}`} accent />
            </>
          )}
          <div style={{ borderTop: hasDiscount ? "1px solid var(--bn-border)" : "none", paddingTop: hasDiscount ? "16px" : "0" }}>
            <Row label={hasDiscount ? "Total pagado" : t("pay.total_paid")} value={`$${finalAmount.toFixed(2)} ${currency}`} big />
          </div>
        </div>
        <div className="md:col-span-2 p-8 md:p-10 flex flex-col items-center justify-center" style={{ background: "var(--bn-surface)", borderLeft: "1px dashed var(--bn-border)" }}>
          <div className="p-4 bg-white rounded">
            <QRCodeCanvas value={`${window.location.origin}/ticket/${booking.id}`} size={160} level="H" />
          </div>
          <div className="bn-eyebrow mt-4 text-center">{t("pay.scan_verify")}</div>
        </div>
      </div>
    </div>
  );
}

function ActionButtons({ booking, shareWhatsApp, t }) {
  return (
    <div className="flex flex-wrap gap-3 justify-center mt-8">
      <button
        onClick={shareWhatsApp}
        disabled={!booking}
        className="bn-btn-accent inline-flex items-center gap-2 disabled:opacity-40"
        data-testid="whatsapp-share-btn"
      >
        <MessageCircle size={16} /> {t("pay.share_wa")}
      </button>
      <button
        onClick={() => window.print()}
        disabled={!booking}
        className="bn-btn-ghost inline-flex items-center gap-2 disabled:opacity-40"
        data-testid="print-ticket-btn"
      >
        <Download size={16} /> {t("pay.print")}
      </button>
    </div>
  );
}

function Row({ label, value, big, accent, muted }) {
  const valueStyle = {
    color: accent ? "var(--bn-accent)" : muted ? "var(--bn-text-muted)" : undefined,
  };
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>{label}</span>
      <span className={big ? "bn-heading text-2xl" : "bn-body-font"} style={valueStyle}>{value}</span>
    </div>
  );
}
